import React from 'react'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import { ProductDataContext } from '../ProductDataContext'
import {
  findInterfaceByItemNo,
  findMechByItemNo,
  findMechByItemNoInMap,
  getColorDisplayName,
  getInterfaceDisplayName,
} from '../products/productUtils'
import addZoomedDragElement from '../utils/addZoomedDragElement'
import ConfigurableProductButton from '../components/ConfigurableProductButton'
import * as ButtonOptionIndexDragDrop from './ButtonOptionIndexDragDrop'
import { InterfaceProductWithMechOptions, RemoteButtonControls } from './ButtonConfigurationScreen'
import './ButtonConfigurationItem.scss'
import { Chip } from '../components/Chip'
import HoverExtraInformation from '../components/HoverExtraInformation'
import { UserType } from '../types/userTypes'
import MechSelector from '../components/MechSelector'
import classNames from 'classnames'
import { IconChevDown } from '../components/icons'
import { AnalyticsContext } from '../analytics/AnalyticsContext'

export interface ButtonConfigurationItemProps {
  item: InterfaceProductWithMechOptions
  isButtonsSection: boolean
  remoteButtonControls: RemoteButtonControls
}

export const ButtonConfigurationItem: React.FC<ButtonConfigurationItemProps> = (props) => {

  const analyticsLogger = React.useContext(AnalyticsContext)

  const { item, isButtonsSection, remoteButtonControls } = props

  const { moreInfo, userType } = React.useContext(ConfiguratorAppContext)
  const { interfaces: buttons, mechs: allMechs, mechMap } = React.useContext(ProductDataContext)

  const { selectedButtonIndex, onSelectButtonOption, configuredButtonIds } = remoteButtonControls
  const { interfaceProduct, mechOptions } = item
  const { image, color, itemNo, description } = interfaceProduct

  const packQuantity = remoteButtonControls.shouldShowPackQuantity ? interfaceProduct.packQuantity : null

  // Set default mech for this interface.
  const mechs = mechOptions.map(it => it.itemNo)//.map(mech => findMechByItemNoInMap(mech, mechMap))
  const defaultMechItemNo = mechs[0] ?? null
  const selectedMechProduct = findMechByItemNoInMap(defaultMechItemNo, mechMap)
  const mechImageUrl: string | undefined = selectedMechProduct?.image
  const mechItemNo = selectedMechProduct?.itemNo ?? null

  const displayName = getInterfaceDisplayName(item.interfaceProduct, mechOptions.length === 1 ? mechOptions[0] ?? null : null)

  const [seeMore, setSeeMore] = React.useState<boolean>(false)

  const isSelected = (
    !!configuredButtonIds[selectedButtonIndex]
      && itemNo === configuredButtonIds[selectedButtonIndex].interfaceItemNo
      && (
        !configuredButtonIds[selectedButtonIndex].mechItemNo
          || (mechOptions.findIndex(it => it.itemNo === configuredButtonIds[selectedButtonIndex].mechItemNo) > -1)
      )
  )

  const ref = React.useRef<HTMLDivElement | null>(null)

  const prevMoreInfoRef = React.useRef<boolean>()
  const prevIsSelectedRef = React.useRef<boolean>(isSelected)

  // scroll to interface when it becomes selected, or when the view type changes
  React.useEffect(() => {
    const prevMoreInfo = prevMoreInfoRef.current
    const prevIsSelected = prevIsSelectedRef.current


    if (isSelected) {
      if (!prevIsSelected || (moreInfo !== prevMoreInfo)) {
        ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }

    prevMoreInfoRef.current = moreInfo
    prevIsSelectedRef.current = isSelected
  }, [isSelected, moreInfo])

  return (
    <div
      ref={ref}
      key={`${itemNo} ${selectedMechProduct?.itemNo || 'no mech'}`}
      className={`ButtonConfigurationItem ${moreInfo ? 'moreInfo' : ''}`}
    >
      <div className="buttonOptionCell" >
        <ConfigurableProductButton
          onClick={() => {
            if (selectedButtonIndex > -1) {
              onSelectButtonOption(selectedButtonIndex, findInterfaceByItemNo(itemNo, buttons), mechs.includes(mechItemNo ?? '') ? findMechByItemNo(mechItemNo, allMechs) : findMechByItemNo(mechs[0] ?? null, allMechs))
            }
          }}
          absoluteSize={isButtonsSection ? '40px' : '80px'}
          x={0}
          y={0}
          baseScale={1}
          selected={isSelected}
          backgroundImage={mechImageUrl}
          foregroundImage={image}
          onDragStart={e => {
            ButtonOptionIndexDragDrop.putData(e, itemNo, mechItemNo, null)
            addZoomedDragElement(e, isButtonsSection ? 1.5 : 0.75, (e.currentTarget as HTMLDivElement))
          }}
          debugColorCode={color}
        />
        {
          !moreInfo ? (
            <>
              <span className="optionLabel">
                {displayName}
              </span>
              {userType === UserType.Electrician && <span className="itemNo">{itemNo}</span>}
            </>
          ) : (
            <div className="spacer" />
          )
        }
        {(userType === UserType.Electrician && packQuantity !== null && packQuantity > 1) && (
          <Chip outline>{packQuantity} pk</Chip>
        )}
      </div>
      {
        moreInfo && (
          <div className="moreInfoDetails">
            <p className="value"><strong>{displayName}</strong></p>
            <p>{description}</p>
            <div className="moreInfoGrid">
              <div className="gridItem">Interface:</div>
              <div className="gridItem value">
                {itemNo}
                {(packQuantity !== null && packQuantity > 1) && (
                  <>
                    <div className="spacer" />
                    <HoverExtraInformation inlineLabel outline showDescription label="i" >This item is only available in a pack of {packQuantity}</HoverExtraInformation>
                  </>
                )}
              </div>
              <>
                <div className="gridItem">Colour:</div>
                <div className="gridItem value">{getColorDisplayName(interfaceProduct.color)}</div>
              </>
            </div>

            {mechs.length === 0 ? (
              <div className="compatibleMechs">
                <span className="value">No compatible mechanisms.</span>
              </div>
            ) : (
              <div className="compatibleMechs">
                <p className={'compatibleMechsHeading'}>
                  Compatible Mechanisms:
                  <span
                    className="seeMore"
                    onClick={() => {
                      analyticsLogger.log({
                        event: 'cta_click',
                        cta_name: seeMore ? 'Hide Details' : 'View Details',
                        cta_link: null,
                        cta_category: 'Text button',
                        cta_location: 'Customise switches'
                      })

                      setSeeMore(!seeMore)
                    }}
                  >
                    <img className="icon" alt="" src={IconChevDown} style={{ transform: seeMore ? 'rotate(0.5turn)' : undefined }} />
                    {seeMore ? 'Hide Details' : 'View Details'}
                  </span>
                </p>

                <div className={classNames('compatibleMechsGrid', { collapsed: !seeMore })}>
                  {mechOptions.map((it) => {
                    return (
                      <MechSelector
                        collapsed={!seeMore}
                        key={it.itemNo}
                        isSelected={configuredButtonIds[selectedButtonIndex]?.interfaceItemNo === itemNo && configuredButtonIds[selectedButtonIndex]?.mechItemNo === it.itemNo}
                        mechProduct={it}
                        onClick={() => onSelectButtonOption(selectedButtonIndex, findInterfaceByItemNo(itemNo, buttons), it)}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )
      }
    </div>
  )
}